// src/App.jsx
import React, { useState, useEffect } from 'react'
import './index.css'
import { AuthProvider, useAuth } from './lib/AuthContext'
import { getRifleProfiles } from './lib/supabase'
import RifleProfileBuilder from './components/RifleProfileBuilder'
import AmmoPage from './components/AmmoPage'
import AuthScreen from './components/AuthScreen'
import TrajectoryPage from './components/TrajectoryPage'
import ProfilePage from './components/ProfilePage'
import UpgradePage from './components/UpgradePage'
import ComparePage from './components/ComparePage'

const NAV = [
  { id: 'rifles',     label: 'Rifles',     icon: RifleIcon     },
  { id: 'ammo',       label: 'Ammo',       icon: AmmoIcon      },
  { id: 'trajectory', label: 'Drop Table', icon: TrajIcon      },
  { id: 'compare',    label: 'Compare',    icon: CompareIcon   },
  { id: 'profile',    label: 'Profile',    icon: ProfileIcon   },
]

function AppInner() {
  const { user, loading } = useAuth()
  const [tab, setTab]              = useState('rifles')
  const [profiles, setProfiles]    = useState([])
  const [activeProfile, setActive] = useState(null)
  const [showAuth, setShowAuth]    = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)

  useEffect(() => {
    if (user) { loadProfiles(); setShowAuth(false) }
  }, [user])

  async function loadProfiles() {
    const { data } = await getRifleProfiles(user.id)
    if (data) {
      setProfiles(data)
      if (data.length > 0 && !activeProfile) setActive(data[0])
    }
  }

  if (loading) return <LoadingScreen />

  return (
    <div style={appStyles.root}>

      {/* Auth Overlay */}
      {showAuth && <AuthScreen onSuccess={() => setShowAuth(false)} />}

      {/* Upgrade Overlay */}
      {showUpgrade && <UpgradePage onClose={() => setShowUpgrade(false)} />}

      {/* Header */}
      <header style={appStyles.header}>
        <div style={appStyles.logoWrap}>
          <div style={appStyles.logoIcon}><CrosshairSVG /></div>
          <div>
            <div style={appStyles.logoName}>DISDownrange</div>
            <div style={appStyles.logoBrand}>by Dynamic Innovative Solutions</div>
          </div>
        </div>
        <div style={appStyles.headerRight}>
          {activeProfile && (
            <div style={appStyles.activeRifle}>
              <span style={appStyles.activeDot} />
              {activeProfile.nickname}
            </div>
          )}
          {!user ? (
            <button style={appStyles.signInBtn} onClick={() => setShowAuth(true)}>Sign In</button>
          ) : (
            <button style={appStyles.signInBtn} onClick={() => setTab('profile')}>
              {user.email?.split('@')[0]}
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main style={appStyles.main}>
        {tab === 'rifles'     && (
          <RifleProfileBuilder
            profiles={profiles}
            onProfilesChange={loadProfiles}
            onSelect={setActive}
            selectedId={activeProfile?.id}
          />
        )}
        {tab === 'ammo'       && <AmmoPage       activeProfile={activeProfile} />}
        {tab === 'trajectory' && <TrajectoryPage activeProfile={activeProfile} onUpgrade={() => setShowUpgrade(true)} />}
        {tab === 'compare'    && <ComparePage    activeProfile={activeProfile} onUpgrade={() => setShowUpgrade(true)} />}
        {tab === 'profile'    && (
          <ProfilePage onUpgrade={() => setShowUpgrade(true)} />
        )}
      </main>

      {/* Bottom Nav */}
      <nav style={appStyles.nav}>
        {NAV.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            style={{ ...appStyles.navBtn, ...(tab === id ? appStyles.navBtnActive : {}) }}
            onClick={() => setTab(id)}
          >
            <Icon active={tab === id} />
            <span style={{ ...appStyles.navLabel, ...(tab === id ? appStyles.navLabelActive : {}) }}>
              {label}
            </span>
          </button>
        ))}
      </nav>

    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  )
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16 }}>
      <CrosshairSVG size={40} />
      <div style={{ fontFamily: 'Courier New, monospace', fontSize: 12, color: 'var(--text-muted)', letterSpacing: '0.1em' }}>LOADING...</div>
    </div>
  )
}

function CrosshairSVG({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="7" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  )
}

function RifleIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="1.5" strokeLinecap="round">
      <rect x="2" y="10" width="16" height="4" rx="1" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <line x1="6" y1="14" x2="6" y2="18" />
      <line x1="10" y1="14" x2="10" y2="16" />
    </svg>
  )
}

function AmmoIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="1.5" strokeLinecap="round">
      <rect x="9" y="4" width="6" height="14" rx="2" />
      <path d="M10 4 Q12 2 14 4" />
      <line x1="9" y1="10" x2="15" y2="10" />
    </svg>
  )
}

function TrajIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="1.5" strokeLinecap="round">
      <path d="M3 18 Q8 18 12 12 T21 6" />
      <line x1="3" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3" y2="18" />
    </svg>
  )
}

function CompareIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="1.5" strokeLinecap="round">
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  )
}

function ProfileIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}

const appStyles = {
  root:    { display: 'flex', flexDirection: 'column', height: '100vh', maxWidth: 'var(--max-width)', margin: '0 auto', position: 'relative' },
  header:  { height: 'var(--header-height)', padding: '0 20px', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100, flexShrink: 0 },
  logoWrap:  { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon:  { width: 34, height: 34, background: 'var(--accent)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0a0a0a', flexShrink: 0 },
  logoName:  { fontSize: 15, fontWeight: 700, letterSpacing: '0.03em', color: 'var(--text-primary)' },
  logoBrand: { fontSize: 9, color: 'var(--accent)', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Courier New, monospace' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  activeRifle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Courier New, monospace', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', padding: '4px 10px', borderRadius: 20 },
  activeDot:   { width: 5, height: 5, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 },
  signInBtn:   { background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', color: 'var(--accent)', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontFamily: 'Courier New, monospace', cursor: 'pointer' },
  main: { flex: 1, overflowY: 'auto', paddingBottom: 'calc(var(--nav-height) + var(--safe-bottom) + 8px)' },
  nav:  { height: 'var(--nav-height)', display: 'flex', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', position: 'sticky', bottom: 0, zIndex: 100, flexShrink: 0, paddingBottom: 'var(--safe-bottom)' },
  navBtn:         { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' },
  navBtnActive:   {},
  navLabel:       { fontSize: 9, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', letterSpacing: '0.06em', textTransform: 'uppercase' },
  navLabelActive: { color: 'var(--accent)' },
}

